import React, { useState } from 'react';

const ArtemisGeneticEvolution = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [expandedCode, setExpandedCode] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const evolutionSteps = [
    {
      id: 0,
      title: '1. Initial Population',
      description: 'Start with diverse code variants',
      icon: '🌱'
    },
    {
      id: 1,
      title: '2. Fitness Evaluation',
      description: 'Score each solution',
      icon: '📊'
    },
    {
      id: 2,
      title: '3. Selection',
      description: 'Choose the best performers',
      icon: '🏆'
    },
    {
      id: 3,
      title: '4. Crossover',
      description: 'Combine best traits',
      icon: '🔀'
    },
    {
      id: 4,
      title: '5. Mutation',
      description: 'Introduce variations',
      icon: '🧬'
    },
    {
      id: 5,
      title: '6. New Generation',
      description: 'Evolved solutions',
      icon: '🚀'
    }
  ];

  const codeExamples = {
    original: {
      title: 'Original Code',
      language: 'python',
      code: `def find_max(arr):
    max_val = arr[0]
    for i in range(len(arr)):
        if arr[i] > max_val:
            max_val = arr[i]
    return max_val`,
      metrics: {
        complexity: 'O(n)',
        readability: '3/5',
        performance: '2/5'
      }
    },
    variant1: {
      title: 'Variant 1: Built-in Function',
      language: 'python',
      code: `def find_max(arr):
    return max(arr) if arr else None`,
      metrics: {
        complexity: 'O(n)',
        readability: '5/5',
        performance: '5/5'
      }
    },
    variant2: {
      title: 'Variant 2: Parallel Processing',
      language: 'python',
      code: `from concurrent.futures import ThreadPoolExecutor
import numpy as np

def find_max(arr):
    if len(arr) < 1000:
        return max(arr)
    
    chunks = np.array_split(arr, 4)
    with ThreadPoolExecutor() as executor:
        chunk_maxes = list(executor.map(max, chunks))
    return max(chunk_maxes)`,
      metrics: {
        complexity: 'O(n/p)',
        readability: '3/5',
        performance: '4/5'
      }
    },
    crossover: {
      title: 'Crossover Result',
      language: 'python',
      code: `def find_max(arr):
    # Combines simplicity and performance
    if not arr:
        return None
    
    # Use built-in for small arrays
    if len(arr) < 1000:
        return max(arr)
    
    # Parallel for large arrays
    return parallel_max(arr)`,
      metrics: {
        complexity: 'O(n) / O(n/p)',
        readability: '4/5',
        performance: '5/5'
      }
    }
  };

  const generationData = [
    {
      generation: 1,
      avgFitness: 3.2,
      bestFitness: 4.1,
      diversity: 0.89
    },
    {
      generation: 2,
      avgFitness: 3.8,
      bestFitness: 4.5,
      diversity: 0.76
    },
    {
      generation: 3,
      avgFitness: 4.2,
      bestFitness: 4.8,
      diversity: 0.65
    },
    {
      generation: 4,
      avgFitness: 4.5,
      bestFitness: 4.9,
      diversity: 0.58
    },
    {
      generation: 5,
      avgFitness: 4.7,
      bestFitness: 4.95,
      diversity: 0.52
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{ 
            fontSize: '48px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #F39C12, #E67E22)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '20px'
          }}>
            🧬 Genetic Programming in Artemis
          </h1>
          <p style={{ fontSize: '20px', opacity: 0.8, maxWidth: '800px', margin: '0 auto' }}>
            How Artemis uses evolutionary algorithms to optimize code through natural selection principles
          </p>
        </div>

        {/* Evolution Process */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          <h2 style={{ marginBottom: '30px', color: '#F39C12' }}>Evolution Process</h2>
          
          {/* Steps */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '40px',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            {evolutionSteps.map((step, idx) => (
              <div
                key={step.id}
                onClick={() => setActiveStep(idx)}
                style={{
                  flex: '1',
                  minWidth: '150px',
                  padding: '20px',
                  background: activeStep === idx ? 'rgba(243, 156, 18, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  border: `2px solid ${activeStep === idx ? '#F39C12' : 'transparent'}`,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>{step.icon}</div>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>{step.title}</h4>
                <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>{step.description}</p>
              </div>
            ))}
          </div>

          {/* AI-Driven Genetic Evolution Flow */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '40px'
          }}>
            <h3 style={{ color: '#F39C12', marginBottom: '20px' }}>🤖 AI-Driven Genetic Operations</h3>
            
            {/* Shared Memory Context */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(155, 89, 182, 0.2), rgba(142, 68, 173, 0.2))',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '30px',
              border: '2px solid rgba(155, 89, 182, 0.4)'
            }}>
              <h4 style={{ color: '#9B59B6', marginBottom: '15px' }}>🧠 Shared Memory & Context</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  <strong>📊 Performance History</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                    Past optimization results, benchmark scores, bottleneck patterns
                  </p>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  <strong>🔍 Code Patterns</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                    Successful transformations, anti-patterns, domain-specific rules
                  </p>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  <strong>🎯 Fitness Weights</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                    User preferences, objective priorities, constraint parameters
                  </p>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  <strong>📚 Evolution History</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                    Successful mutations, crossover patterns, convergence data
                  </p>
                </div>
              </div>
            </div>

            {/* AI Agents Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              {/* Mutation Agent */}
              <div
                onClick={() => setSelectedAgent(selectedAgent === 'mutation' ? null : 'mutation')}
                style={{
                  background: selectedAgent === 'mutation' ? 'rgba(231, 76, 60, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${selectedAgent === 'mutation' ? '#E74C3C' : 'rgba(255, 255, 255, 0.1)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <h4 style={{ color: '#E74C3C', marginBottom: '10px' }}>🧬 Mutation Agent</h4>
                <p style={{ fontSize: '14px', marginBottom: '15px' }}>Introduces controlled variations</p>
                
                {selectedAgent === 'mutation' && (
                  <div style={{ marginTop: '15px', fontSize: '13px' }}>
                    <strong>Mutation Strategies:</strong>
                    <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                      <li>Replace algorithms (O(n²) → O(n log n))</li>
                      <li>Change data structures (array → hash map)</li>
                      <li>Add optimizations (caching, memoization)</li>
                      <li>Modify parameters (buffer sizes, thresholds)</li>
                      <li>Inject parallelism (async, threading)</li>
                    </ul>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <strong>Context Used:</strong>
                      <br />• Performance bottlenecks from profiling
                      <br />• Similar optimizations that worked before
                      <br />• Language-specific optimization patterns
                    </div>
                  </div>
                )}
              </div>

              {/* Crossover Agent */}
              <div
                onClick={() => setSelectedAgent(selectedAgent === 'crossover' ? null : 'crossover')}
                style={{
                  background: selectedAgent === 'crossover' ? 'rgba(39, 174, 96, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${selectedAgent === 'crossover' ? '#27AE60' : 'rgba(255, 255, 255, 0.1)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <h4 style={{ color: '#27AE60', marginBottom: '10px' }}>🔀 Crossover Agent</h4>
                <p style={{ fontSize: '14px', marginBottom: '15px' }}>Combines successful traits</p>
                
                {selectedAgent === 'crossover' && (
                  <div style={{ marginTop: '15px', fontSize: '13px' }}>
                    <strong>Crossover Techniques:</strong>
                    <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                      <li>Merge algorithmic approaches</li>
                      <li>Combine optimization strategies</li>
                      <li>Hybrid data structure usage</li>
                      <li>Mix error handling patterns</li>
                      <li>Blend performance & readability</li>
                    </ul>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <strong>Context Used:</strong>
                      <br />• Fitness scores of parent solutions
                      <br />• Complementary strengths analysis
                      <br />• Successful combination patterns
                    </div>
                  </div>
                )}
              </div>

              {/* Selection Agent */}
              <div
                onClick={() => setSelectedAgent(selectedAgent === 'selection' ? null : 'selection')}
                style={{
                  background: selectedAgent === 'selection' ? 'rgba(52, 152, 219, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${selectedAgent === 'selection' ? '#3498DB' : 'rgba(255, 255, 255, 0.1)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <h4 style={{ color: '#3498DB', marginBottom: '10px' }}>🏆 Selection Agent</h4>
                <p style={{ fontSize: '14px', marginBottom: '15px' }}>Chooses elite solutions</p>
                
                {selectedAgent === 'selection' && (
                  <div style={{ marginTop: '15px', fontSize: '13px' }}>
                    <strong>Selection Criteria:</strong>
                    <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                      <li>Multi-objective fitness scoring</li>
                      <li>Diversity preservation</li>
                      <li>Elitism (keep top performers)</li>
                      <li>Tournament selection</li>
                      <li>Adaptive pressure control</li>
                    </ul>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <strong>Context Used:</strong>
                      <br />• User-defined fitness weights
                      <br />• Historical convergence rates
                      <br />• Population diversity metrics
                    </div>
                  </div>
                )}
              </div>

              {/* Evaluation Agent */}
              <div
                onClick={() => setSelectedAgent(selectedAgent === 'evaluation' ? null : 'evaluation')}
                style={{
                  background: selectedAgent === 'evaluation' ? 'rgba(155, 89, 182, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${selectedAgent === 'evaluation' ? '#9B59B6' : 'rgba(255, 255, 255, 0.1)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <h4 style={{ color: '#9B59B6', marginBottom: '10px' }}>📊 Evaluation Agent</h4>
                <p style={{ fontSize: '14px', marginBottom: '15px' }}>Scores fitness objectively</p>
                
                {selectedAgent === 'evaluation' && (
                  <div style={{ marginTop: '15px', fontSize: '13px' }}>
                    <strong>Evaluation Metrics:</strong>
                    <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                      <li>Performance benchmarks</li>
                      <li>Memory profiling</li>
                      <li>Code quality analysis</li>
                      <li>Test coverage</li>
                      <li>Security scanning</li>
                    </ul>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <strong>Context Used:</strong>
                      <br />• Target performance requirements
                      <br />• Resource constraints
                      <br />• Domain-specific benchmarks
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Evolution Flow Visualization */}
            <svg width="100%" height="400" viewBox="0 0 1200 400">
              {/* Shared Memory at top */}
              <rect x="200" y="20" width="800" height="60" fill="rgba(155, 89, 182, 0.2)" stroke="#9B59B6" strokeWidth="2" rx="10"/>
              <text x="600" y="55" fill="#9B59B6" textAnchor="middle" fontSize="16" fontWeight="bold">🧠 Shared Memory & Context</text>
              
              {/* Memory connections to agents */}
              <path d="M 300 80 L 300 120" stroke="#9B59B6" strokeWidth="1" strokeDasharray="3,3"/>
              <path d="M 500 80 L 500 120" stroke="#9B59B6" strokeWidth="1" strokeDasharray="3,3"/>
              <path d="M 700 80 L 700 120" stroke="#9B59B6" strokeWidth="1" strokeDasharray="3,3"/>
              <path d="M 900 80 L 900 120" stroke="#9B59B6" strokeWidth="1" strokeDasharray="3,3"/>

              {/* Population (Gen N) */}
              <g transform="translate(100, 200)">
                <rect x="-40" y="-60" width="80" height="120" fill="rgba(52, 152, 219, 0.1)" stroke="#3498DB" strokeWidth="2" rx="10"/>
                <circle cx="0" cy="-30" r="12" fill="#3498DB"/>
                <circle cx="0" cy="0" r="12" fill="#3498DB"/>
                <circle cx="0" cy="30" r="12" fill="#3498DB"/>
                <text x="0" y="-80" fill="white" textAnchor="middle" fontSize="14" fontWeight="bold">Gen N</text>
                <text x="0" y="75" fill="white" textAnchor="middle" fontSize="11">Population</text>
              </g>

              {/* Selection Agent */}
              <g transform="translate(300, 200)">
                <rect x="-60" y="-40" width="120" height="80" fill="rgba(52, 152, 219, 0.2)" stroke="#3498DB" strokeWidth="2" rx="10"/>
                <text x="0" y="-10" fill="#3498DB" textAnchor="middle" fontSize="14" fontWeight="bold">🏆 Selection</text>
                <text x="0" y="10" fill="white" textAnchor="middle" fontSize="12">Agent</text>
                <text x="0" y="30" fill="white" textAnchor="middle" fontSize="10" opacity="0.7">Elite + Diverse</text>
              </g>

              {/* Selected Parents */}
              <g transform="translate(500, 200)">
                <circle cx="-20" cy="0" r="12" fill="#E74C3C"/>
                <circle cx="20" cy="0" r="12" fill="#E74C3C"/>
                <text x="0" y="-30" fill="white" textAnchor="middle" fontSize="12">Parents</text>
              </g>

              {/* Crossover Agent */}
              <g transform="translate(700, 150)">
                <rect x="-60" y="-40" width="120" height="80" fill="rgba(39, 174, 96, 0.2)" stroke="#27AE60" strokeWidth="2" rx="10"/>
                <text x="0" y="-10" fill="#27AE60" textAnchor="middle" fontSize="14" fontWeight="bold">🔀 Crossover</text>
                <text x="0" y="10" fill="white" textAnchor="middle" fontSize="12">Agent</text>
                <text x="0" y="30" fill="white" textAnchor="middle" fontSize="10" opacity="0.7">Combine traits</text>
              </g>

              {/* Mutation Agent */}
              <g transform="translate(700, 250)">
                <rect x="-60" y="-40" width="120" height="80" fill="rgba(231, 76, 60, 0.2)" stroke="#E74C3C" strokeWidth="2" rx="10"/>
                <text x="0" y="-10" fill="#E74C3C" textAnchor="middle" fontSize="14" fontWeight="bold">🧬 Mutation</text>
                <text x="0" y="10" fill="white" textAnchor="middle" fontSize="12">Agent</text>
                <text x="0" y="30" fill="white" textAnchor="middle" fontSize="10" opacity="0.7">Add variations</text>
              </g>

              {/* Offspring */}
              <g transform="translate(900, 200)">
                <circle cx="0" cy="-20" r="12" fill="#F39C12"/>
                <circle cx="0" cy="0" r="12" fill="#F39C12"/>
                <circle cx="0" cy="20" r="12" fill="#F39C12"/>
                <text x="0" y="-40" fill="white" textAnchor="middle" fontSize="12">Offspring</text>
              </g>

              {/* Evaluation Agent */}
              <g transform="translate(1050, 200)">
                <rect x="-50" y="-40" width="100" height="80" fill="rgba(155, 89, 182, 0.2)" stroke="#9B59B6" strokeWidth="2" rx="10"/>
                <text x="0" y="-10" fill="#9B59B6" textAnchor="middle" fontSize="14" fontWeight="bold">📊 Eval</text>
                <text x="0" y="10" fill="white" textAnchor="middle" fontSize="12">Agent</text>
                <text x="0" y="30" fill="white" textAnchor="middle" fontSize="10" opacity="0.7">Score fitness</text>
              </g>

              {/* Arrows */}
              <path d="M 140 200 L 240 200" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              <path d="M 360 200 L 440 200" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              <path d="M 540 200 L 640 180" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              <path d="M 540 200 L 640 220" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              <path d="M 760 150 L 860 180" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              <path d="M 760 250 L 860 220" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              <path d="M 940 200 L 1000 200" stroke="white" strokeWidth="2" markerEnd="url(#arrowhead)"/>
              
              {/* Feedback loop */}
              <path d="M 1050 240 Q 600 350 100 240" fill="none" stroke="#F39C12" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#arrowhead)"/>
              <text x="600" y="340" fill="#F39C12" textAnchor="middle" fontSize="12">Next Generation (with updated shared memory)</text>
              
              {/* Memory update arrow */}
              <path d="M 1050 160 Q 1100 50 1000 50" stroke="#9B59B6" strokeWidth="2" strokeDasharray="3,3" markerEnd="url(#arrowhead)"/>
              <text x="1080" y="100" fill="#9B59B6" fontSize="10" textAnchor="middle">Update</text>

              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="white"/>
                </marker>
              </defs>
            </svg>
          </div>
        </div>

        {/* Code Evolution Example */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '30px',
          marginBottom: '40px'
        }}>
          {Object.entries(codeExamples).map(([key, example]) => (
            <div
              key={key}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                padding: '30px',
                border: expandedCode === key ? '2px solid #F39C12' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => setExpandedCode(expandedCode === key ? null : key)}
            >
              <h3 style={{ 
                margin: '0 0 20px 0',
                color: key === 'crossover' ? '#27AE60' : '#F39C12'
              }}>
                {example.title}
              </h3>
              
              <pre style={{
                background: 'rgba(0, 0, 0, 0.5)',
                padding: '20px',
                borderRadius: '8px',
                fontSize: '14px',
                overflow: 'auto',
                marginBottom: '20px'
              }}>
                <code>{example.code}</code>
              </pre>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px'
              }}>
                <span>Complexity: <strong style={{ color: '#3498DB' }}>{example.metrics.complexity}</strong></span>
                <span>Readability: <strong style={{ color: '#E74C3C' }}>{example.metrics.readability}</strong></span>
                <span>Performance: <strong style={{ color: '#27AE60' }}>{example.metrics.performance}</strong></span>
              </div>
            </div>
          ))}
        </div>

        {/* Fitness Evolution Chart */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          <h2 style={{ marginBottom: '30px', color: '#F39C12' }}>Fitness Evolution Over Generations</h2>
          
          <div style={{ position: 'relative', height: '400px' }}>
            <svg width="100%" height="100%" viewBox="0 0 800 400">
              {/* Grid lines */}
              {[0, 1, 2, 3, 4, 5].map(i => (
                <g key={i}>
                  <line
                    x1="60"
                    y1={350 - i * 60}
                    x2="740"
                    y2={350 - i * 60}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="1"
                  />
                  <text x="40" y={355 - i * 60} fill="white" fontSize="12" textAnchor="end">
                    {i}
                  </text>
                </g>
              ))}
              
              {/* X-axis labels */}
              {generationData.map((gen, idx) => (
                <text
                  key={idx}
                  x={100 + idx * 150}
                  y="380"
                  fill="white"
                  fontSize="12"
                  textAnchor="middle"
                >
                  Gen {gen.generation}
                </text>
              ))}

              {/* Best fitness line */}
              <polyline
                fill="none"
                stroke="#27AE60"
                strokeWidth="3"
                points={generationData.map((gen, idx) => 
                  `${100 + idx * 150},${350 - gen.bestFitness * 60}`
                ).join(' ')}
              />
              
              {/* Average fitness line */}
              <polyline
                fill="none"
                stroke="#3498DB"
                strokeWidth="3"
                points={generationData.map((gen, idx) => 
                  `${100 + idx * 150},${350 - gen.avgFitness * 60}`
                ).join(' ')}
              />

              {/* Diversity line */}
              <polyline
                fill="none"
                stroke="#E74C3C"
                strokeWidth="2"
                strokeDasharray="5,5"
                points={generationData.map((gen, idx) => 
                  `${100 + idx * 150},${350 - gen.diversity * 5 * 60}`
                ).join(' ')}
              />

              {/* Data points */}
              {generationData.map((gen, idx) => (
                <g key={idx}>
                  <circle
                    cx={100 + idx * 150}
                    cy={350 - gen.bestFitness * 60}
                    r="5"
                    fill="#27AE60"
                  />
                  <circle
                    cx={100 + idx * 150}
                    cy={350 - gen.avgFitness * 60}
                    r="5"
                    fill="#3498DB"
                  />
                </g>
              ))}

              {/* Legend */}
              <g transform="translate(550, 30)">
                <line x1="0" y1="0" x2="30" y2="0" stroke="#27AE60" strokeWidth="3"/>
                <text x="35" y="5" fill="white" fontSize="12">Best Fitness</text>
                
                <line x1="0" y1="20" x2="30" y2="20" stroke="#3498DB" strokeWidth="3"/>
                <text x="35" y="25" fill="white" fontSize="12">Avg Fitness</text>
                
                <line x1="0" y1="40" x2="30" y2="40" stroke="#E74C3C" strokeWidth="2" strokeDasharray="5,5"/>
                <text x="35" y="45" fill="white" fontSize="12">Diversity</text>
              </g>
            </svg>
          </div>
        </div>

        {/* Key Concepts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px'
        }}>
          <div style={{
            background: 'rgba(52, 152, 219, 0.1)',
            borderRadius: '16px',
            padding: '30px',
            border: '2px solid rgba(52, 152, 219, 0.3)'
          }}>
            <h3 style={{ color: '#3498DB', marginBottom: '20px' }}>🎯 Fitness Functions</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>Performance metrics (speed, memory)</li>
              <li>Code quality (readability, maintainability)</li>
              <li>Security vulnerabilities</li>
              <li>Test coverage and reliability</li>
              <li>Multi-objective optimization</li>
            </ul>
          </div>

          <div style={{
            background: 'rgba(231, 76, 60, 0.1)',
            borderRadius: '16px',
            padding: '30px',
            border: '2px solid rgba(231, 76, 60, 0.3)'
          }}>
            <h3 style={{ color: '#E74C3C', marginBottom: '20px' }}>🔀 Genetic Operators</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
              <li><strong>Selection:</strong> Tournament, roulette wheel</li>
              <li><strong>Crossover:</strong> Single-point, uniform</li>
              <li><strong>Mutation:</strong> Random changes, guided mutations</li>
              <li><strong>Elitism:</strong> Preserve best solutions</li>
              <li><strong>Migration:</strong> Exchange between populations</li>
            </ul>
          </div>

          <div style={{
            background: 'rgba(39, 174, 96, 0.1)',
            borderRadius: '16px',
            padding: '30px',
            border: '2px solid rgba(39, 174, 96, 0.3)'
          }}>
            <h3 style={{ color: '#27AE60', marginBottom: '20px' }}>⚡ Advantages</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>Explores vast solution spaces efficiently</li>
              <li>Finds unexpected optimizations</li>
              <li>Balances multiple objectives</li>
              <li>Continuously improves over time</li>
              <li>Adapts to changing requirements</li>
            </ul>
          </div>
        </div>

        {/* Artemis Integration */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1), rgba(230, 126, 34, 0.1))',
          borderRadius: '16px',
          padding: '40px',
          marginTop: '40px',
          border: '2px solid rgba(243, 156, 18, 0.3)'
        }}>
          <h3 style={{ color: '#F39C12', marginBottom: '20px' }}>
            🚀 How Artemis Uses Genetic Programming
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 10px 0' }}>1. Code Analysis</h4>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                AI analyzes your code to identify optimization opportunities and generates initial population
              </p>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 10px 0' }}>2. Evolution Cycles</h4>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                Multiple LLMs generate variants, which are evolved through genetic operators
              </p>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 10px 0' }}>3. Real-world Testing</h4>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                Each variant is tested with actual benchmarks and unit tests for validation
              </p>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '20px',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 10px 0' }}>4. Continuous Learning</h4>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                Best solutions are stored and used to improve future optimizations
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtemisGeneticEvolution;